import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, Card, CheckboxGroup, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n } from '../../i18n.js';
import { display, useStore, type StoreDetails } from '../catalog/shared.js';
import { dinarsToMinor, minorToDinars } from '../shipping/CarriersPage.js';

type Overview = Schemas['CashOverview'];
type Balance = Overview['carriers'][number];
type Outstanding = Schemas['OutstandingCollections'];
type Remittance = Schemas['Remittance'];

/** Tunis calendar date of today (yyyy-mm-dd). */
function today(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Tunis' }).format(new Date());
}

/** Cash in hand, what each courier still holds, handovers (remittances) and their history. */
export function PaymentsPage() {
  const { storeId = '' } = useParams();
  const { t, locale, formatPrice, formatDateTime } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [history, setHistory] = useState<Remittance[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [cash, remittances] = await Promise.all([
      unwrap(api.GET('/admin-api/v1/stores/{storeId}/cash', { params: { path: { storeId } } })),
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/remittances', {
          params: { path: { storeId }, query: {} },
        }),
      ),
    ]);
    setOverview(cash);
    setHistory(remittances.items);
  }, [storeId]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [load, t]);

  const failed = storeError ?? error;
  if (failed !== null) return <Alert tone="error">{failed}</Alert>;
  if (store === null || overview === null) return <Loading />;
  const canManage = store.permissions.includes('orders.manage');
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.payments.title')}</h1>
      <p>{t('admin.payments.intro')}</p>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      <div className="admin-kpis">
        <div className="admin-kpi">
          <strong className="admin-kpi__value">
            {t('admin.payments.inHand', { amount: formatPrice(overview.inHand.minor) })}
          </strong>
        </div>
        <div className="admin-kpi">
          <strong className="admin-kpi__value">
            {t('admin.payments.withCarriers', {
              amount: formatPrice(overview.withCarriers.minor),
            })}
          </strong>
        </div>
      </div>
      {overview.carriers.map((carrier) => (
        <Card key={carrier.carrierId} labelledBy={`carrier-${carrier.carrierId}`}>
          <h2 id={`carrier-${carrier.carrierId}`} className="mrigl-subheading" dir="auto">
            {carrier.name}
          </h2>
          <p>
            {carrier.outstanding === 0
              ? t('admin.payments.nothingOwed')
              : t('admin.payments.owed', {
                  name: carrier.name,
                  amount: formatPrice(carrier.owed.minor),
                  count: carrier.outstanding,
                })}
          </p>
          {carrier.lastRemittanceAt === null ? null : (
            <p className="mrigl-muted">
              {t('admin.payments.lastRemittance', {
                date: formatDateTime(carrier.lastRemittanceAt),
              })}
            </p>
          )}
          {canManage && carrier.outstanding > 0 ? (
            open === carrier.carrierId ? (
              <RemittanceForm
                store={store}
                carrier={carrier}
                onDone={async () => {
                  setOpen(null);
                  setNotice(t('admin.payments.saved'));
                  await load();
                }}
              />
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  setNotice(null);
                  setOpen(carrier.carrierId);
                }}
              >
                {t('admin.payments.remit', { name: carrier.name })}
              </Button>
            )
          ) : null}
        </Card>
      ))}
      <Card labelledBy="remittances">
        <h2 id="remittances" className="mrigl-subheading">
          {t('admin.payments.history')}
        </h2>
        {history.length === 0 ? (
          <p className="mrigl-muted">{t('admin.payments.none')}</p>
        ) : (
          <ul className="mrigl-list">
            {history.map((item) => (
              <li key={item.remittanceId}>
                {t('admin.payments.remittance', {
                  date: formatDateTime(item.receivedAt),
                  name: item.carrierName,
                  net: formatPrice(item.net.minor),
                  gross: formatPrice(item.gross.minor),
                  fees: formatPrice(item.fees.minor),
                })}
                {item.note === null ? null : (
                  <>
                    {' — '}
                    <span dir="auto">{item.note}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function RemittanceForm({
  store,
  carrier,
  onDone,
}: {
  readonly store: StoreDetails;
  readonly carrier: Balance;
  readonly onDone: () => Promise<void>;
}) {
  const { t, formatPrice } = useI18n();
  const [outstanding, setOutstanding] = useState<Outstanding | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [fees, setFees] = useState('');
  const [date, setDate] = useState(today());
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/carriers/{carrierId}/outstanding', {
        params: { path: { storeId: store.storeId, carrierId: carrier.carrierId } },
      }),
    )
      .then((result) => {
        setOutstanding(result);
        setSelected(result.items.map((item) => item.entryId));
        setFees(minorToDinars(result.suggestedFees.minor));
      })
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
  }, [store.storeId, carrier.carrierId, t]);

  if (outstanding === null)
    return error === null ? <Loading /> : <Alert tone="error">{error}</Alert>;
  const gross = outstanding.items
    .filter((item) => selected.includes(item.entryId))
    .reduce((sum, item) => sum + Number(item.amount.minor), 0);
  const feesMinor = dinarsToMinor(fees);
  const net = feesMinor === null ? null : gross - Number(feesMinor);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (selected.length === 0) {
      setError(t('admin.payments.error.select'));
      return;
    }
    if (feesMinor === null) {
      setError(t('admin.cash.error.amount'));
      return;
    }
    setBusy(true);
    try {
      await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/carriers/{carrierId}/remittances', {
          params: { path: { storeId: store.storeId, carrierId: carrier.carrierId } },
          body: {
            entryIds: selected,
            fees: { minor: feesMinor, currency: 'TND' },
            receivedAt: new Date(`${date}T12:00:00+01:00`).toISOString(),
            note: note.trim() === '' ? null : note.trim(),
          },
        }),
      );
      await onDone();
    } catch (failure) {
      setError(
        failure instanceof ApiError && failure.code === 'validation-failed'
          ? t('admin.payments.error.stale')
          : errorMessage(t, failure),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-stack"
      aria-label={t('admin.payments.remit', { name: carrier.name })}
      onSubmit={(event) => void save(event)}
    >
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <CheckboxGroup
        legend={t('admin.payments.remit', { name: carrier.name })}
        options={outstanding.items.map((item) => ({
          value: item.entryId,
          label: [
            t('admin.payments.order', { number: String(item.orderNumber) }),
            ...(item.shipmentNumber === null
              ? []
              : [t('admin.cash.parcel', { number: String(item.shipmentNumber) })]),
            formatPrice(item.amount.minor),
          ].join(' — '),
        }))}
        values={selected}
        onChange={setSelected}
      />
      <TextField
        label={t('admin.payments.fees')}
        hint={t('admin.payments.feesHint', {
          amount: formatPrice(outstanding.suggestedFees.minor),
        })}
        inputMode="decimal"
        dir="ltr"
        value={fees}
        onChange={(event) => {
          setFees(event.target.value);
        }}
      />
      <TextField
        type="date"
        label={t('admin.payments.receivedAt')}
        required
        value={date}
        onChange={(event) => {
          setDate(event.target.value);
        }}
      />
      <TextField
        label={t('admin.cash.note')}
        maxLength={300}
        dir="auto"
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
        }}
      />
      {net === null ? null : (
        <p>
          <strong>{t('admin.payments.net', { amount: formatPrice(String(net)) })}</strong>
        </p>
      )}
      <Button type="submit" busy={busy}>
        {t('admin.payments.save')}
      </Button>
    </form>
  );
}
